import type { CSSProperties, FC, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { Drawer } from '@mui/material';
import { clsx } from 'clsx';
import { motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import { SandboxHeader, useMobileSandboxBarHeight } from 'components/SandboxHeader/SandboxHeader';
import { useAssistantEnabled } from 'features/onboarding/lib/useAssistantEnabled';
import { useAssistantPanelReservation } from 'features/onboarding/lib/useAssistantPanelReservation';
import { AssistantLauncherButton } from 'features/onboarding/ui/AssistantLauncherButton';
import { useUpgradePromoBannerProps } from 'features/upgradePromo/lib/useUpgradePromoBannerProps';
import { UpgradePromo } from 'features/upgradePromo/ui/UpgradePromo';
import { UpgradePromoBanner } from 'features/upgradePromo/ui/UpgradePromoBanner';
import { useMobileNavStore } from 'shared/lib/mobileNav/useMobileNavStore';
import { usePromo } from 'shared/lib/promo/usePromo';
import { useQueryParams } from 'shared/lib/queryParams/useQueryParams';
import { useScreenSize } from 'shared/lib/screen/useScreenSize';
import { SIDEBAR_TRANSITION, SIDEBAR_WIDTH_VAR, useSidebarWidth } from 'shared/lib/sidebar/useSidebarStore';

import { NavBar } from './NavBar';

const LayoutContext = createContext<HTMLDivElement | null>(null);

export const TwoPaneLayout: FC<PropsWithChildren> = ({ children }) => {
  const { promoHeader, promoHeaderHeight, promoHeaderRef, activePromoKind } = usePromo();
  const upgradeBannerProps = useUpgradePromoBannerProps();
  // Legacy chain wins on conflict; upgrade-promo only fills the slot when no
  // legacy banner is active. See docs/plans/2026-05-14-001-feat-upgrade-promo-banner-top-placement-plan.md.
  const showUpgradeBanner = activePromoKind === null && upgradeBannerProps !== null;
  const topBanner = showUpgradeBanner ? (
    <UpgradePromoBanner ref={promoHeaderRef} {...upgradeBannerProps} />
  ) : (
    promoHeader
  );
  // using state instead of ref because we need to update it after children are rendered
  const [layoutEl, setLayoutEl] = useState<HTMLDivElement | null>(null);
  const { action } = useQueryParams();
  const isCreateOrder = action === 'create';
  const assistantEnabled = useAssistantEnabled();
  useAssistantPanelReservation({ enabled: assistantEnabled });
  const { isMobile } = useScreenSize();
  const sidebarWidth = useSidebarWidth();
  const prefersReducedMotion = useReducedMotion();
  const sandboxBarHeight = useMobileSandboxBarHeight();
  const isNavOpen = useMobileNavStore((state) => state.isOpen);
  const closeNav = useMobileNavStore((state) => state.close);
  const { pathname } = useLocation();

  // biome-ignore lint/correctness/useExhaustiveDependencies: navigating from inside the drawer should leave it behind
  useEffect(() => closeNav(), [pathname, closeNav]);

  return (
    <LayoutContext.Provider value={layoutEl}>
      <div
        className={clsx('assistant-panel-reserves-width fixed top-0 w-assistant-reserved', {
          'pointer-events-none': !topBanner,
          'z-[1301]': isCreateOrder, // MUI backdrop has 1300, and we need the header to be above
          'z-50': !isCreateOrder,
        })}
      >
        {topBanner}
        <SandboxHeader />
      </div>
      <motion.div
        animate={{ [SIDEBAR_WIDTH_VAR]: `${sidebarWidth}px` }}
        className="box-border flex h-full bg-white max-md:h-dvh"
        initial={false}
        style={
          {
            paddingTop: `${promoHeaderHeight + sandboxBarHeight}px`,
            [SIDEBAR_WIDTH_VAR]: `${sidebarWidth}px`,
          } as CSSProperties
        }
        transition={prefersReducedMotion ? { duration: 0 } : SIDEBAR_TRANSITION}
      >
        {isMobile ? (
          <Drawer open={isNavOpen} PaperProps={{ sx: { overflow: 'visible' } }} onClose={closeNav}>
            <NavBar />
          </Drawer>
        ) : (
          <div className="flex h-full max-h-screen">
            <NavBar />
          </div>
        )}
        <div
          ref={setLayoutEl}
          className="assistant-panel-reserves relative flex flex-auto flex-col overflow-auto md:min-w-page"
          // The sidebar keeps its width on mobile, where it renders as a drawer rather than a column.
          style={
            isMobile
              ? undefined
              : { maxWidth: `calc(100vw - var(${SIDEBAR_WIDTH_VAR}) - var(--assistant-panel-reserved, 0px))` }
          }
        >
          {children}
        </div>
      </motion.div>
      <UpgradePromo />
      {assistantEnabled && !isMobile && <AssistantLauncherButton />}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  return useContext(LayoutContext);
};
